// Aligné sur l'énumération PDP (ITS v2) — mêmes noms et valeurs que le backend.
export enum CanonicalInvoiceStatus {
  Unknown               = 0,
  PdpRejected           = 1,
  PdpSubmitted          = 2,
  PdpIssuedByPlatform   = 3,
  PdpReceivedByPlatform = 4,
  PdpMadeAvailable      = 5,
  PdpInHand             = 6,
  PdpRefused            = 7,
  PdpPartiallyApproved  = 8,
  PdpSuspended          = 9,
  PdpCompleted          = 10,
  PdpDisputed           = 11,
  PdpApproved           = 12,
  PdpPaymentSent        = 13,
  PdpPaymentReceived    = 14,
  ItsSentToPdp          = 15,
  ItsReceivedFromPdp    = 16,
  ItsNewDocument        = 17,
  ItsSentToPdpFailed    = 18,
  PdpRejectedByBuyer    = 19,
  PdpNone               = 20,
}

/** Casse PascalCase alignée sur BCO.EInvoice.ServiceAgent.Models.PdpAttachmentDto (réponse API CRM, pas passthrough brut). */
export interface InvoiceAttachmentDto {
  Id: string;
  FileName: string;
  MimeType?: string;
}

export interface PdpLifecycleEventDto {
  id?: string;
  transmissionId?: string;
  providerStatus?: string;
  canonicalStatus?: CanonicalInvoiceStatus | string;
  source?: string;
  occurredAtUtc: string;
  displayMessage?: string;
  traceId?: string;
}

/**
 * Casse PascalCase alignée sur BCO.EInvoice.ServiceAgent.Models.ReceivedInvoiceDto (réponse API CRM
 * pour GET EFacture/ReceivedInvoices/{id}, un DTO fortement typé — pas le passthrough JObject brut
 * utilisé par GET .../lifecycle, qui lui reste en camelCase car il retransmet tel quel le JSON du
 * prestataire sans repasser par notre pipeline Newtonsoft).
 * NOTE : lifecycleEvents reste tel quel (non renommé) — PdpLifecycleEventDto ci-dessus ne correspond
 * pas à la forme réelle de BCO.EInvoice.ServiceAgent.Models.PdpLifecycleEventDto (EventType/
 * OccurredAtUtc/Description) ; ce champ est de toute façon toujours vide aujourd'hui et le composant
 * retombe systématiquement sur un appel getLifecycle() séparé — à corriger dans un lot dédié.
 */
export interface ReceivedInvoiceDto {
  Id: string;
  ProviderInvoiceId: string;
  BuyerTaxId: string;
  Status: CanonicalInvoiceStatus;
  ReceivedAtUtc: string;
  UpdatedAtUtc: string;
  TotalAmountWithoutVat: number | null;
  TotalAmount: number | null;
  TotalToPay: number | null;
  PaidAmount: number | null;
  RemainingAmount: number | null;
  MonetaryUnit: string;
  lifecycleEvents: PdpLifecycleEventDto[];
  Attachments?: InvoiceAttachmentDto[];
}

export interface ReceivedInvoiceDtoPagedResult {
  items: ReceivedInvoiceDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

// ── DTOs CRM (format réponse API CRM) ────────────────────────
export interface ReceivedInvoiceCrmDto {
  Id: string;
  ProviderInvoiceId: string;
  TenantId: string;
  CabinetId: number;
  NumeroFacture: string;
  EmetteurNom: string;
  EmetteurSiren: string;
  EmetteurEmail: string;
  DateEmission: string | null;
  DateReception: string | null;
  DateEcheance: string | null;
  MontantHT: number;
  MontantTVA: number;
  MontantTTC: number;
  Devise: string;
  Statut: string;
  CanonicalStatus: number;
  /** Statut PDP brut (PdpInHand, PdpApproved, …) — pilote l'affichage des actions. */
  ProviderStatus: string;
  BuyerTaxId: string;
  BuyerCountryCode: string;
  PdfUrl: string | null;
  XmlUrl: string | null;
  ObjetFacture: string | null;
  Timeline: any[];
}

export interface ReceivedInvoiceListCrmResult {
  Items: ReceivedInvoiceCrmDto[];
  TotalCount: number;
  TotalMontantHT: number;
  TotalMontantTTC: number;
}

/** Codes de motif de refus (valeur entière envoyée au PDP). */
export enum RefuseReasonCode {
  AUTRE = 1,
  TX_TVA_ERR = 2,
  MONTANTTOTAL_ERR = 3,
  CALCUL_ERR = 4,
  NON_CONFORME = 5,
  DOUBLON = 6,
  DEST_ERR = 7,
  TRANSAC_INC = 8,
  EMMET_INC = 9,
  CONTRAT_TERM = 10,
  DOUBLE_FACT = 11,
  CMD_ERR = 12,
  ADR_ERR = 13,
  REF_CT_ABSENT = 14,
}

export const REFUSE_REASON_OPTIONS: { label: string; value: number }[] = [
  { label: 'Autre', value: RefuseReasonCode.AUTRE },
  { label: 'Erreur de taux de TVA', value: RefuseReasonCode.TX_TVA_ERR },
  { label: 'Erreur sur le montant total', value: RefuseReasonCode.MONTANTTOTAL_ERR },
  { label: 'Erreur de calcul', value: RefuseReasonCode.CALCUL_ERR },
  { label: 'Non conforme', value: RefuseReasonCode.NON_CONFORME },
  { label: 'Doublon', value: RefuseReasonCode.DOUBLON },
  { label: 'Erreur destinataire', value: RefuseReasonCode.DEST_ERR },
  { label: 'Transaction inconnue', value: RefuseReasonCode.TRANSAC_INC },
  { label: 'Émetteur inconnu', value: RefuseReasonCode.EMMET_INC },
  { label: 'Contrat terminé', value: RefuseReasonCode.CONTRAT_TERM },
  { label: 'Double facturation', value: RefuseReasonCode.DOUBLE_FACT },
  { label: 'Erreur commande', value: RefuseReasonCode.CMD_ERR },
  { label: 'Erreur adresse', value: RefuseReasonCode.ADR_ERR },
  { label: 'Référence contrat manquante', value: RefuseReasonCode.REF_CT_ABSENT },
];

/** Codes de motif de suspension (valeur entière envoyée au PDP, même convention 1-based que RefuseReasonCode). */
export enum SuspendReasonCode {
  AUTRE = 1,
  JUSTIF_ABS = 2,
  COORD_BANC_ERR = 3,
  CMD_ERR = 4,
  SIRET_ERR = 5,
  CODE_ROUTAGE_ERR = 6,
  REF_CT_ABSENT = 7,
  REF_ERR = 8,
}

export const SUSPEND_REASON_OPTIONS: { label: string; value: number }[] = [
  { label: 'Autre', value: SuspendReasonCode.AUTRE },
  { label: 'Justificatif absent', value: SuspendReasonCode.JUSTIF_ABS },
  { label: 'Coordonnées bancaires erronées', value: SuspendReasonCode.COORD_BANC_ERR },
  { label: 'Erreur de commande', value: SuspendReasonCode.CMD_ERR },
  { label: 'SIRET erroné', value: SuspendReasonCode.SIRET_ERR },
  { label: 'Code de routage erroné', value: SuspendReasonCode.CODE_ROUTAGE_ERR },
  { label: 'Référence contrat absente', value: SuspendReasonCode.REF_CT_ABSENT },
  { label: 'Référence erronée', value: SuspendReasonCode.REF_ERR },
];

/** Actions de cycle de vie autorisées par le PDP pour une facture (projection booléenne d'InvoiceAction). */
/** Casse PascalCase alignée sur BCO.EInvoice.ServiceAgent.Models.AvailableActionsDto (réponse API CRM). */
export interface AvailableActions {
  CanSubmit?: boolean;
  CanAccept?: boolean;
  CanApprove?: boolean;
  CanApprovePartially?: boolean;
  CanDispute?: boolean;
  CanSuspend?: boolean;
  CanComplete?: boolean;
  CanSendPayment?: boolean;
  CanSendPartialPayment?: boolean;
  CanReceivePayment?: boolean;
  CanReceivePartialPayment?: boolean;
  CanRefuse?: boolean;
}

